import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import type { Person } from '@/components/Combobox/PersonSelectorCombobox';
import { CATEGORY_FILTER } from '@/components/Combobox/PersonSelectorCombobox/-query/type';
import { Spacer, VStack } from '@/components/Layout';
import { FullScreenOverlay } from '@/components/Loading';
import { WorkerSelect } from '@/components/Select/WorkerSelect';
import { Text } from '@/components/Typography/Text';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { apiClient } from '@/lib/api/client';
import { getDataOrThrowApiError, handleApiError } from '@/lib/api/error';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { useAllowIndividualRequestOperationPackageQuery } from '@/routes/_individual/individual/-query/useAllowIndividualRequestOperationPackageQuery';
import { useNavigateBackOrFallback } from '../../-hooks/useNavigateBackOrFallback';
import { useGetEligibleTarget, useStartOperationPackageByEligibleTarget } from '../../-query';
export const Route = createFileRoute('/_individual/individual/operation-package/$id/target/$targetId/')({
  component: RouteComponent
});
function RouteComponent() {
  const {
    i18n
  } = useTranslation();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.operationPackage'
  });
  const tc = useCommonTranslation();
  const {
    id,
    targetId
  } = Route.useParams();
  const navigate = Route.useNavigate();
  const {
    data: operationPackage
  } = useAllowIndividualRequestOperationPackageQuery(id);
  const {
    data: eligibleTargets
  } = useGetEligibleTarget(id);
  const selectedTarget = eligibleTargets.find(target => target.id === targetId);
  const [targetPerson, setTargetPerson] = useState<Person | undefined>(undefined);
  const {
    mutate,
    isPending
  } = useStartOperationPackageByEligibleTarget(id, targetId, {
    onSuccess: async data => {
      try {
        // タスク詳細APIを叩いて次のタスクIDを取得
        const response = await apiClient.GET('/v2/self-service/tasks/{id}', {
          params: {
            path: {
              id: data.operationPackageInstanceId
            }
          }
        });
        const taskDetail = getDataOrThrowApiError(response);

        // NotStartedまたはInProgressのタスクを探す
        let nextTaskId: string | null = null;
        for (const step of taskDetail.steps) {
          for (const task of step.tasks) {
            if (task.status === 'NotStarted' || task.status === 'InProgress') {
              nextTaskId = task.taskInstanceId;
              break;
            }
          }
          if (nextTaskId) break;
        }

        // 次のタスクが見つかった場合はフォームページへ遷移
        if (nextTaskId) {
          navigate({
            to: '/individual/task/$id/form/$formId',
            params: {
              id: data.operationPackageInstanceId,
              formId: nextTaskId
            }
          });
        } else {
          // 次のタスクが見つからない場合はタスクページへ遷移
          navigate({
            to: `/individual/task/${data.operationPackageInstanceId}`
          });
        }
      } catch {
        // エラー時はタスクページへ遷移
        navigate({
          to: `/individual/task/${data.operationPackageInstanceId}`
        });
      }
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      toast({
        description: message,
        variant: 'destructive'
      });
    }
  });

  // ガード: 不正なtargetIdの場合はStep 1にリダイレクト（履歴に不正URLを残さない）
  useEffect(() => {
    if (!selectedTarget) {
      navigate({
        to: `/individual/operation-package/${id}`,
        replace: true
      });
    }
  }, [selectedTarget, navigate, id]);
  const handleBack = useNavigateBackOrFallback(`/individual/operation-package/${id}`);
  if (!selectedTarget) {
    return null;
  }
  const startPackage = () => {
    if (!targetPerson?.id) return;
    mutate({
      targetPersonId: targetPerson.id
    });
  };
  return <VStack className="py-3 px-5 gap-3 items-start max-w-dialog-lg mx-auto w-full bg-neutral-fill min-h-full">
      <ActionButton icon="ChevronLeft" iconColor="muted" variant="ghost" className="p-0 text-neutral-muted-on-fill font-semibold h-fit" onClick={handleBack}>
        {t('navigateToSelectTarget')}
      </ActionButton>
      <VStack className="gap-2">
        <Text variant="h2">{operationPackage.name}</Text>
        <Text variant="labelLarge">{selectedTarget.name}</Text>
      </VStack>
      <div className="py-3 w-full">
        <Separator />
      </div>
      <Spacer variant="s" />
      <VStack className="gap-2 w-full">
        <Text variant="labelLarge" className="font-semibold text-neutral-muted-on-fill">
          {t('page.startOperationPackage.requestFor')}
        </Text>
        <WorkerSelect value={targetPerson} onChange={setTargetPerson} category={CATEGORY_FILTER.ALL_ACTIVE} excludeNotHaveWorkerEmail />
      </VStack>
      <Spacer variant="s" />
      <ActionButton onClick={startPackage} className="w-full" disabled={!targetPerson}>
        {tc('action.next')}
      </ActionButton>
      {isPending && <FullScreenOverlay />}
    </VStack>;
}